<template>
	<Modals
		class="apis-modal"
		:display="display"
		:title="`${integration.name} APIs`"
		@show="closeModal"
	>
		<template #modal-content>
			<Search
				class="apis-modal__search"
				v-if="showSearchComponent"
				v-model.lazy="searchQuery"
				variant="toggle-icons"
				placeholder="Search APIs"
				:enableTimeOut="false"
				data-test="search"
			/>
			<div class="apis-modal__count">
				<ResultCounter :counter="resultCounter" v-if="hasResults" />
			</div>

			<ul class="apis-modal__list">
				<li
					class="apis-modal__list-item"
					v-for="(item, index) in integrationsApis"
					:key="index"
					data-test="integrationsList"
				>
					<Label
						:for="uid"
						class="apis-modal__search-label"
						:labelText="`${item.apiType} | ${item.apiVersion}`"
					/>
				</li>
			</ul>
			<Spinner :processor="{ loading: loading }" data-test="spinner" />
			<NoResults
				class="apis-modal__no-results"
				v-if="!integrationsApis.length && !loading"
				data-test="noResults"
			/>
		</template>
		<template #modal-footer>
			<Button
				className="btn--primary"
				labelText="Close"
				:btnClick="closeModal"
				ref="closeBtn"
			/>
		</template>
	</Modals>
</template>

<script>
import Button from '@/components/forms/button/button';
import Modals from '@/components/shells/modal/modals';
import Search from '@/components/forms/search/search';
import Spinner from '@/components/visual/spinner/spinner';
import NoResults from '@/site-components/no-results/no-results';
import Label from '@/components/forms/label/label';
import ResultCounter from '@/site-components/result-counter/result-counter';
import { mapActions, mapGetters } from 'vuex';
import {
	MODULE_NAME,
	APIS,
	SET_INTEGRATIONS_APIS
} from './apis-mutation-types';

import _ from 'lodash';
export default {
	name: 'ApisModal',
	components: {
		Button,
		Modals,
		Search,
		Spinner,
		NoResults,
		Label,
		ResultCounter
	},
	model: {
		value: 'display',
		event: 'close'
	},
	props: {
		display: {
			type: Boolean,
			default: false
		},
		integration: {
			type: Object,
			default: () => {}
		}
	},
	data() {
		return {
			searchQuery: '',
			loading: false,
			integrationsApis: []
		};
	},
	computed: {
		...mapGetters(MODULE_NAME, [APIS]),
		resultCounter() {
			const totalCount = this.integrationsApis.length || 0;
			return totalCount;
		},
		hasResults() {
			return this.resultCounter > 0;
		},
		showSearchComponent() {
			if (this.integrationsApis.length && !this.loading) return true;
			return _.get(this[APIS], 'totalCount', 0) > 0 && !this.loading;
		}
	},
	methods: {
		...mapActions(MODULE_NAME, [SET_INTEGRATIONS_APIS]),

		async fetchData() {
			this.loading = true;
			await this[SET_INTEGRATIONS_APIS](this.integration);
			this.loading = false;
		},
		setIntegrationsApis() {
			this.integrationsApis = _.get(this[APIS], 'items', []);
		},
		closeModal() {
			this.$emit('close');
		},
		filterIntegrationsApis(searchValue) {
			if (this.integrationsApis.length) {
				let apis = this.integrationsApis.reduce(
					(apisList, currentApi) => {
						let apiType = currentApi.apiType.toLowerCase();
						let apiVersion = currentApi.apiVersion.toLowerCase();

						if (
							apiType.includes(searchValue) ||
							apiVersion.includes(searchValue)
						) {
							apisList.push(currentApi);
						}
						return apisList;
					},
					[]
				);
				this.integrationsApis = apis;
			}
		},
		generateApis() {
			const apis = {
				page: 1,
				items: this.integration.apis,
				countPerPage: 250,
				totalPageCount: 1,
				totalCount: this.integration.apis.length,
				statusCode: 200
			};
			this.$store.commit('thirdpartyApis/setApis', apis);
		}
	},
	watch: {
		display: {
			async handler(val) {
				if (val) {
					if (_.get(this.integration, 'apis', []).length > 0) {
						this.generateApis();
					} else {
						await this.fetchData();
					}
				} else {
					this.$store.commit('thirdpartyApis/setApis', {});
				}
			}
		},
		searchQuery(value) {
			if (!value) {
				this.setIntegrationsApis();
			} else {
				this.filterIntegrationsApis(value);
			}
		},
		[APIS]: {
			deep: true,
			handler() {
				this.setIntegrationsApis();
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.apis-modal {
	&__search {
		margin-bottom: $gutter-md;
	}

	&__count {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		margin-bottom: $gutter-lg;
	}

	&__list {
		margin-bottom: $gutter-lg;
		max-height: 200px;
		@include scrollbar;
		overflow-y: auto;
	}

	&__list-item {
		&:not(:last-child) {
			margin-bottom: $gutter-md;
		}
	}
}
</style>
